/**
 * Carrier-provider — provider-neutral discriminator + canonical const.
 *
 * Single source of truth for "which live-rate carrier integrations exist".
 * Locked here so the `'shippo'` literal that previously sat in 8 separate
 * places (`lib/shipping/types.ts`, `money.ts`, `checkout-intent.ts`,
 * `listing-create.ts`, `listing-patch.ts`, `assert-live-rate-eligible.ts`,
 * `carrier-registry.ts`, and two DB CHECK constraints) cannot drift.
 *
 * Adding a second carrier (`'sendcloud'`, `'easypost'`) means:
 *   1. extend {@link CARRIER_PROVIDERS} below
 *   2. compile breaks at every `switch (provider)` site with a
 *      `default: unreachable(provider)` exhaustiveness probe
 *   3. keep the two DB CHECK constraints in sync (manual — Postgres CHECK
 *      cannot import a TS const). Comment-pointers live on the constraints
 *      themselves; a fitness test parses the migration to flag drift.
 *
 * @see lib/shipping/types.ts — domain-side import
 * @see .tondocs/shippo/PLAN.md §K.6 (provider-neutral rename)
 * @see .tondocs/skills/type-driven-design.md §1, §2, §6 (ADT, illegal-states, SSOT)
 */

/**
 * Exhaustive list of carrier-provider integration ids. The single producer of
 * `CarrierProvider` values — every read site past parse trusts the shape.
 *
 * KEEP IN SYNC WITH:
 *   - `listings.carrier_provider` CHECK in
 *     `supabase/migrations/20260623120000_k6_bundled_currency_pin_snapshots.sql`
 *   - `seller_orders.carrier_provider` CHECK in same migration
 *   - any fitness test that parses the migration regex (see PLAN §K.6 9.a)
 */
export const CARRIER_PROVIDERS = ["shippo"] as const;

/**
 * Carrier-provider integration that supplies live-rate shipments.
 *
 * Single value today — designed for extension. When a second provider lands,
 * grep every `switch` on this discriminator across the project and audit each
 * `default: unreachable(provider)` probe per the left-fix discipline
 * ([[feedback_left_fix_audit_downstream_branches]]).
 */
export type CarrierProvider = (typeof CARRIER_PROVIDERS)[number];

// Compile-time exhaustiveness pin — same pattern as REFUND_STATUSES in
// refund-status.ts. Adding a value to the array without the union (or vice
// versa) fails the build. The union is derived from the array via
// `(typeof CARRIER_PROVIDERS)[number]` so this looks tautological, but the
// pin is meaningful when downstream callers cast a string to CarrierProvider:
// if such a cast widens the type, the assertion fails.
type _AssertKindsCoverUnion =
  (typeof CARRIER_PROVIDERS)[number] extends CarrierProvider ? true : never;
type _AssertNoExtraKinds =
  CarrierProvider extends (typeof CARRIER_PROVIDERS)[number] ? true : never;

const _pinKindsCoverUnion: _AssertKindsCoverUnion = true;
const _pinNoExtraKinds: _AssertNoExtraKinds = true;
void _pinKindsCoverUnion;
void _pinNoExtraKinds;
